import type { ReactNode } from 'react';
import { useContext } from 'react';
import isMobile from 'ismobilejs';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import type { Theme as LocalTheme } from '@trinity/chat-components/types/theme';
import { useThemedStyles } from '../Theme';
import { IsSidePanelMessageContext } from './context';
import { useChatInstanceAuthor } from './hooks/useChatInstanceAuthor';
import { UserAuthor } from './Message.Author';
import {
  authorSpacing,
  extendedMessageWidth,
  messageStyles,
  sidePanelAuthorCss,
  sidePanelUserAuthorWrapCss,
} from './Message.styles';

type Props = {
  classes?: {
    body?: SerializedStyles;
    root?: SerializedStyles;
  };
  value: ReactNode;
};

export const UserMessage = ({
  classes = {
    body: undefined,
    root: undefined,
  },
  value,
}: Props) => {
  const device = isMobile();
  const isSidePanel = useContext(IsSidePanelMessageContext);
  const styles = useStyles(isSidePanel);
  const { showAuthor } = useAvatarPanel();

  const author = useChatInstanceAuthor();

  return (
    <div css={css(styles.root, classes.root)}>
      <div
        css={css(
          styles.body,
          classes.body,
          shouldExpandWidth(value) ? css({ maxWidth: extendedMessageWidth }) : undefined
        )}
      >
        {value}
      </div>
      {showAuthor && !device.phone && (
        <div css={styles.authorWrap}>
          <UserAuthor author={author} css={styles.author} />
        </div>
      )}
    </div>
  );
};

const authorWrapCss = css([
  messageStyles.authorWrap,
  {
    marginLeft: authorSpacing,
  },
]);

const sidePanelAuthorWrapCss = css([
  sidePanelUserAuthorWrapCss,
  {
    marginLeft: 12,
  },
]);

const authorCss = css(messageStyles.author);
const sidePanelAuthorSizeCss = css(sidePanelAuthorCss);

const rootCss = css([
  messageStyles.root,
  {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
]);

const bodyCss = (theme: LocalTheme.Theme) =>
  css([
    messageStyles.body,
    {
      backgroundColor: theme.palette.white.main,
      border: `2px solid ${theme.palette.gray.light2}`,
    },
  ]);

const useStyles = (isSidePanel: boolean) =>
  useThemedStyles({
    author: isSidePanel ? sidePanelAuthorSizeCss : authorCss,
    authorWrap: isSidePanel ? sidePanelAuthorWrapCss : authorWrapCss,
    body: bodyCss,
    root: rootCss,
  });

function shouldExpandWidth(value: Props['value']) {
  if (typeof value !== 'string') {
    return false;
  }
  return value.length > 200;
}
